import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Select } from "../../components/ui/Select";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { SearchInput } from "../../components/SearchInput";
import { useApi } from "../../context/ApiContext";
import { io, Socket } from "socket.io-client";

interface ChatMessage {
 _id: string;
 conversation?: string;
 sender: {
  _id: string;
  name: string;
  email: string;
 };
 senderType: "customer" | "agent";
 message: string;
 createdAt: string;
}

interface ChatConversation {
 _id: string;
 user?: {
  _id: string;
  name: string;
  email: string;
 };
 email?: string;
 status: "open" | "in-progress" | "resolved" | "closed";
 assignedTo?: {
  _id: string;
  name: string;
 };
 subject?: string;
 internalNote?: string;
 lastMessageAt: string;
 unreadCount: number;
 messages?: ChatMessage[];
}

export const HelpDesk: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [conversations, setConversations] = useState<ChatConversation[]>([]);
 const [selectedConversation, setSelectedConversation] =
  useState<ChatConversation | null>(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [debouncedSearch, setDebouncedSearch] = useState("");
 const [status, setStatus] = useState("");
 const [replyMessage, setReplyMessage] = useState("");
 const [internalNote, setInternalNote] = useState("");
 const [savingNote, setSavingNote] = useState(false);
 const socketRef = useRef<Socket | null>(null);
 const messagesEndRef = useRef<HTMLDivElement>(null);

 // Initialize websocket connection
 useEffect(() => {
  const token = localStorage.getItem("token");
  if (token) {
   // Get backend URL based on current hostname (for localhost subdomain testing)
   let backendUrl = process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5100";
   
   if (typeof window !== "undefined" && !process.env.REACT_APP_BACKEND_BASE_URL) {
    const hostname = window.location.hostname;
    if (hostname.includes("localhost") || hostname.includes("local")) {
     backendUrl = `http://${hostname}:5100`;
    } else {
     backendUrl = `${window.location.protocol}//${hostname}`;
    }
   }
   
   const normalizedUrl = backendUrl.replace(/\/$/, "");
   
   const socket = io(normalizedUrl, {
    path: "/socket.io/chat",
    auth: { token },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
   });

   socket.on("connect", () => {
    // Join all conversation rooms when connected
    conversations.forEach((conv) => {
     socket.emit("join-conversation", conv._id);
    });
   });

   socket.on("new-message", (message: ChatMessage) => {
    // Update selected conversation if it matches
    setSelectedConversation((prev) => {
     if (!prev || prev._id !== message.conversation) return prev;
     // Check if message already exists (avoid duplicates)
     const exists = prev.messages?.some((m) => m._id === message._id || (m._id.startsWith("temp-") && m.message === message.message));
     if (exists) {
      // Replace temp message with real one
      return {
       ...prev,
       messages: prev.messages?.map((m) => 
        m._id.startsWith("temp-") && m.message === message.message ? message : m
       ) || [message],
      };
     }
     return {
      ...prev,
      messages: [...(prev.messages || []), message],
     };
    });

    // Update conversations list to show new message
    setConversations((prev) => {
     const updated = prev.map((conv) => {
      if (conv._id === message.conversation) {
       return {
        ...conv,
        lastMessageAt: message.createdAt,
        unreadCount: message.senderType === "customer" ? (conv.unreadCount || 0) + 1 : 0,
       };
      }
      return conv;
     });
     
     // If conversation not in list, refresh to get it
     const conversationExists = updated.some((conv) => conv._id === message.conversation);
     if (!conversationExists && message.senderType === "customer") {
      // New conversation with customer message, refresh list
      setTimeout(() => fetchConversations(), 500);
     }
     
     return updated;
    });

    // Also update selected conversation's lastMessageAt if it matches
    setSelectedConversation((prev) => {
     if (!prev || prev._id !== message.conversation) return prev;
     return {
      ...prev,
      lastMessageAt: message.createdAt,
     };
    });
   });

   socketRef.current = socket;

   return () => {
    socket.close();
   };
  }
  
  // Return no-op cleanup function if no token
  return () => {
   // No cleanup needed if socket wasn't created
  };
 }, []);

 // Join conversation room when selected
 useEffect(() => {
  if (socketRef.current && selectedConversation) {
   socketRef.current.emit("join-conversation", selectedConversation._id);
  }
 }, [selectedConversation]);

 // Join all conversation rooms when conversations change
 useEffect(() => {
  if (socketRef.current && socketRef.current.connected) {
   conversations.forEach((conv) => {
    socketRef.current?.emit("join-conversation", conv._id);
   });
  }
 }, [conversations]);

 // Handler for when search is ready from SearchInput component
 const handleSearchReady = useCallback((searchQuery: string) => {
  setDebouncedSearch(searchQuery);
 }, []);

 // Memoize placeholder to prevent SearchInput from rerendering
 const searchPlaceholder = useMemo(() => t("admin.helpDesk.searchPlaceholder"), [t]);

 const fetchConversations = useCallback(async () => {
  try {
   setLoading(true);
   setError(null);
   const params = new URLSearchParams({
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(status && { status }),
   });
   const response = await api.get(`/chat/admin/conversations?${params.toString()}`);
   setConversations(response.data.conversations || []);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.helpDesk.errors.fetch"));
  } finally {
   setLoading(false);
  }
 }, [api, debouncedSearch, status, t]);

 useEffect(() => {
  fetchConversations();
 }, [fetchConversations]);

 // Auto-scroll to bottom when messages change
 useEffect(() => {
  if (selectedConversation?.messages && selectedConversation.messages.length > 0) {
   setTimeout(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
   }, 100);
  }
 }, [selectedConversation?.messages]);

 const handleSelectConversation = async (conversation: ChatConversation) => {
  try {
   const response = await api.get(
    `/chat/conversations/${conversation._id}/messages`
   );
   setSelectedConversation({
    ...conversation,
    messages: response.data.messages || [],
   });
   // Load internal note if it exists
   setInternalNote(conversation.internalNote || "");
   // Scroll to bottom after loading messages
   setTimeout(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
   }, 100);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.helpDesk.errors.loadMessages"));
  }
 };

 const handleStatusChange = async (
  conversationId: string,
  newStatus: string
 ) => {
  try {
   await api.put(`/chat/admin/conversations/${conversationId}/status`, {
    status: newStatus,
   });
   setConversations(
    conversations.map((c) =>
     c._id === conversationId ? { ...c, status: newStatus as any } : c
    )
   );
   if (selectedConversation?._id === conversationId) {
    setSelectedConversation({
     ...selectedConversation,
     status: newStatus as any,
    });
   }
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.helpDesk.errors.update"));
  }
 };

 const handleSaveInternalNote = async () => {
  if (!selectedConversation) return;

  try {
   setSavingNote(true);
   const response = await api.put(
    `/chat/admin/conversations/${selectedConversation._id}/internal-note`,
    { internalNote }
   );
   
   // Update selected conversation with new internal note
   setSelectedConversation({
    ...selectedConversation,
    internalNote: response.data.internalNote || "",
   });
   
   // Update conversations list
   setConversations(
    conversations.map((c) =>
     c._id === selectedConversation._id
      ? { ...c, internalNote: response.data.internalNote || "" }
      : c
    )
   );
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.helpDesk.errors.update"));
  } finally {
   setSavingNote(false);
  }
 };

 const handleSendReply = async (e?: React.FormEvent) => {
  if (e) {
   e.preventDefault();
  }
  if (!selectedConversation || !replyMessage.trim() || !socketRef.current) return;

  try {
   const messageText = replyMessage.trim();
   const conversationId = selectedConversation._id;
   setReplyMessage("");

   // Optimistically add message to UI
   const tempMessage: ChatMessage = {
    _id: `temp-${Date.now()}`,
    conversation: conversationId,
    sender: {
     _id: "",
     name: t("admin.helpDesk.you"),
     email: "",
    },
    senderType: "agent",
    message: messageText,
    createdAt: new Date().toISOString(),
   };

   setSelectedConversation((prev) => {
    if (!prev) return prev;
    return {
     ...prev,
     messages: [...(prev.messages || []), tempMessage],
    };
   });

   // Send via websocket for real-time delivery
   socketRef.current.emit("send-message", {
    conversationId,
    message: messageText,
   });

   // Also send via API as backup
   try {
    await api.post(
     `/chat/conversations/${conversationId}/messages`,
     {
      message: messageText,
     }
    );
   } catch (apiErr) {
    // If API call fails but websocket succeeded, that's okay
    console.warn("API send message failed, but websocket may have succeeded:", apiErr);
   }
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.helpDesk.errors.sendMessage"));
  }
 };

 const getStatusColor = (status: string) => {
  switch (status) {
   case "open":
    return "bg-blue-100 text-blue-800";
   case "in-progress":
    return "bg-yellow-100 text-yellow-800";
   case "resolved":
    return "bg-green-100 text-green-800";
   case "closed":
    return "bg-gray-100 text-gray-800";
   default:
    return "bg-gray-100 text-gray-800";
  }
 };

 if (loading && conversations.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <Heading size="xl">{t("admin.helpDesk.title")}</Heading>

    {error && <Alert variant="error">{error}</Alert>}

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
     {/* Conversations List */}
     <div className="lg:col-span-1">
      <Card padding="lg">
       <Stack spacing="md">
        <Heading size="lg">{t("admin.helpDesk.conversations")}</Heading>

        <div className="space-y-2">
         <div key="search-input-wrapper">
          <SearchInput
           key="admin-helpdesk-search-input"
           instanceId="admin-helpdesk-search-input"
           initialValue=""
           onSearchReady={handleSearchReady}
           debounceMs={500}
           placeholder={searchPlaceholder}
          />
         </div>
         <Select
          options={[
           { value: "", label: t("admin.helpDesk.allStatuses") },
           { value: "open", label: t("admin.helpDesk.open") },
           { value: "in-progress", label: t("admin.helpDesk.inProgress") },
           { value: "resolved", label: t("admin.helpDesk.resolved") },
           { value: "closed", label: t("admin.helpDesk.closed") },
          ]}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
         />
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
         {conversations.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
           {t("admin.helpDesk.noConversations")}
          </div>
         ) : (
          conversations.map((conv) => (
           <div
            key={conv._id}
            onClick={() => handleSelectConversation(conv)}
            className={`p-3 rounded cursor-pointer border-2 transition ${
             selectedConversation?._id === conv._id
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 hover:border-gray-300"
            }`}
           >
            <div className="flex items-start justify-between gap-2">
             <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-gray-900 truncate">
               {conv.user?.name || conv.email || t("admin.helpDesk.guest")}
              </div>
              <div className="text-xs text-gray-500 truncate">
               {conv.subject || t("admin.helpDesk.noSubject")}
              </div>
              <div className="text-xs text-gray-400 mt-1">
               {new Date(conv.lastMessageAt).toLocaleDateString()}
              </div>
             </div>
             {conv.unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center flex-shrink-0">
               {conv.unreadCount}
              </span>
             )}
            </div>
           </div>
          ))
         )}
        </div>
       </Stack>
      </Card>
     </div>

     {/* Conversation Detail */}
     <div className="lg:col-span-2">
      {selectedConversation ? (
       <Card padding="lg">
        <Stack spacing="md">
         <div className="flex items-center justify-between">
          <div>
           <Heading size="lg">
            {selectedConversation.user?.name ||
             selectedConversation.email ||
             t("admin.helpDesk.guest")}
           </Heading>
           <div className="text-sm text-gray-500">
            {selectedConversation.email}
           </div>
          </div>
          <select
           value={selectedConversation.status}
           onChange={(e) =>
            handleStatusChange(
             selectedConversation._id,
             e.target.value
            )
           }
           className={`px-3 py-1 text-xs font-semibold border border-gray-300 ${getStatusColor(
            selectedConversation.status
           )}`}
          >
           <option value="open">{t("admin.helpDesk.open")}</option>
           <option value="in-progress">{t("admin.helpDesk.inProgress")}</option>
           <option value="resolved">{t("admin.helpDesk.resolved")}</option>
           <option value="closed">{t("admin.helpDesk.closed")}</option>
          </select>
         </div>

         {/* Messages */}
         <div className="bg-gray-50 rounded p-4 h-64 overflow-y-auto space-y-4">
          {selectedConversation.messages &&
          selectedConversation.messages.length === 0 ? (
           <div className="text-center text-gray-500">
            {t("admin.helpDesk.noMessages")}
           </div>
          ) : (
           selectedConversation.messages?.map((msg) => {
            const isAgent = msg.senderType === "agent";
            return (
             <div
              key={msg._id}
              className={`flex ${
               isAgent ? "justify-end" : "justify-start"
              }`}
             >
              <div
               className={`max-w-xs p-3 ${
                isAgent
                 ? "bg-blue-600 text-white"
                 : "bg-white text-gray-900 border border-gray-200"
               }`}
              >
               {!isAgent && (
                <div className="text-xs font-semibold mb-1 text-gray-600">
                 {msg.sender.name}
                </div>
               )}
               <div className="text-sm">{msg.message}</div>
               <div
                className={`text-xs mt-1 ${
                 isAgent ? "text-blue-100" : "text-gray-500"
                }`}
               >
                {new Date(msg.createdAt).toLocaleTimeString()}
               </div>
              </div>
             </div>
            );
           })
          )}
          <div ref={messagesEndRef} />
         </div>

         {/* Reply Form */}
         <form onSubmit={handleSendReply}>
          <Stack spacing="sm">
           <textarea
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            onKeyDown={(e) => {
             // Enter sends the message, Shift+Enter creates new line
             if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendReply();
             }
            }}
            placeholder={t("admin.helpDesk.replyPlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
           />
           <Button type="submit" disabled={!replyMessage.trim()}>
            {t("admin.helpDesk.sendReply")}
           </Button>
          </Stack>
         </form>

         {/* Internal Notes */}
         <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
           {t("admin.helpDesk.internalNotes")}
          </label>
          <textarea
           value={internalNote}
           onChange={(e) => setInternalNote(e.target.value)}
           placeholder={t("admin.helpDesk.internalNotesPlaceholder")}
           className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
           rows={2}
          />
          <div className="mt-2">
           <Button
            onClick={handleSaveInternalNote}
            loading={savingNote}
            variant="primary"
            size="sm"
           >
            {t("admin.helpDesk.saveNote")}
           </Button>
          </div>
         </div>
        </Stack>
       </Card>
      ) : (
       <Card padding="lg">
        <div className="text-center text-gray-500 py-8">
         {t("admin.helpDesk.selectConversation")}
        </div>
       </Card>
      )}
     </div>
    </div>
   </Stack>
  </Container>
 );
};

export default HelpDesk;
